import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { useMissingDocs } from "../../../../hooks/useMissingDocs";
import {
  profileSectionTitle,
  stepsInMyProfileStage,
} from "../../../../models/vacancy";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { isStageOneActivated, isStageTwoActivated, isThirdStageActivated } from "../../../../services/candidateJobsService";
import { useAppSelector } from "../../../../store";
import { getStageName } from "../../../../utils/dataParser";
import Warning from "../../../UI/Icons/Warning";
interface Stage {
  stage_number: number;
  stage_name?: string;
  value?: string;
}

interface MappedStage {
  id: number;
  name: string;
}
/**
 * ActivationAlert Component
 *
 * This React functional component displays an alert message to the user
 * if their personal details verification status is not "verified".
 * The alert informs the user about their current onboarding stage and
 * prompts them to complete the required steps to proceed.
 *
 * @returns {JSX.Element} A JSX element containing the alert message if the
 * personal details are not verified, or null otherwise.
 *
 * @remarks
 * - The component uses the `useAppSelector` hook to access the `personaldetails`
 *   from the Redux store's `onboarding` state.
 * - The alert includes a clickable "Complete Stage 1" text for user interaction.
 *
 * @example
 * ```tsx
 * <ActivationAlert />
 * ```
 */
const ActivationAlert: React.FC = () => {
  const profileData = useAppSelector(
    (state) => state.onboarding.personaldetails
  );
  const candidateProfileStages = useAppSelector((state) => state.onboarding.candidateProfileSteps)

  const navigation = useNavigate();

  const completionstatus = useAppSelector(
    (state) => state.onboarding?.completedstatus
  );

  const missingDocs = useMissingDocs();

  const currentStage = profileData?.current_stage ? profileData?.current_stage : null;

  const getMissingStages = (): { names: string[]; numbers: number[] } => {
    const missingStageNames: string[] = [];
    const missingStageNumbers: number[] = [];
    

    stepsInMyProfileStage()?.forEach((stage: Stage) => {
      const mappedStage: MappedStage = {
        id: parseInt(stage.stage_number.toString(), 10),
        name: stage.stage_name || stage.value || "",
      };
      let isStageActivated = false;
      if(mappedStage.id === 1){
        isStageActivated = isStageOneActivated(candidateProfileStages, mappedStage.id) ?? false; 
      } else if(mappedStage.id === 2){
        isStageActivated = isStageTwoActivated(candidateProfileStages,  mappedStage.id) ?? false;
      } else if(mappedStage.id === 2){
        isStageActivated = isThirdStageActivated(candidateProfileStages) ?? false;
      }
      

      const statusOfCompletion =
        completionstatus?.[mappedStage.name as keyof typeof completionstatus];
      if (!isStageActivated) {
        missingStageNames.push(mappedStage.name);
        missingStageNumbers.push(mappedStage.id);
      }
    });

    return { names: missingStageNames, numbers: missingStageNumbers };
  };

  const isPersonalDetailsHaveResume = useMemo(() => {
    if(profileData?.resume === null || profileData === null) {
      return true;
    }
    return false;
  }, [profileData]);

  const formatStageNames = (names: string[]): string => {
    const formatted = names.map((name) =>
      name
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    );

    if (formatted.length === 0) return "";
    if (formatted.length === 1) return formatted[0];
    if (formatted.length === 2) return `${formatted[0]} and ${formatted[1]}`;

    return `${formatted.slice(0, -1).join(", ")} and ${formatted[formatted.length - 1]}`;
  };

  const getStageNamesByStageNumber = (stageNumber: number) => {
    const stages = stepsInMyProfileStage();
    const result: string[] = [];

    const groupedStages = stages.filter(
      (stage) => stage.stage_number === stageNumber
    );

    // ✅ Additional logic for resume and missing docs
    const personalDetailsCompleted = completionstatus?.personal_details === 1;
    const qualificationsCompleted = completionstatus?.qualification === 1;

    groupedStages.forEach((stage) => {
      const statusOfCompletion =
        completionstatus?.[stage.stage_name as keyof typeof completionstatus];
        if (statusOfCompletion !== 1) {
          result.push(stage.stage_name || "");
        }

        if (stage.stage_name === "personal_details" && personalDetailsCompleted && isPersonalDetailsHaveResume) {
          if (!result.includes("personal_details")) {
            result.push("personal_details");
          }
        }

        if (stage.stage_name === "qualification" && qualificationsCompleted && missingDocs.length > 0) {
          if (!result.includes("qualification")) {
            result.push("qualification");
          }
        }
    });

    // ✅ Format result
    // const missingStageNames = result
    //   .map((name) =>
    //     name
    //       .split("_")
    //       .map((word, index) =>
    //         index === 0
    //           ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    //           : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    //       )
    //       .join(" ")
    //   )
    //   .join(" and ");

    const missingStageNames = formatStageNames(result);

    return missingStageNames;
  };

  const missingStages = getMissingStages();
  const missingStageNumbers = missingStages.numbers;

  const handleStageRedirect = () => {
    navigation(AUTH_ROUTE.MY_PROFILE);
  };

  const checkStageSubmittedOrNot = (stageNumber: number | null) => {
    if(currentStage === stageNumber){
      return true;
    } else{
      return false;
    } 
  }

  const alertMessage = useMemo(() => {
    if (missingStageNumbers.length === 0) return null;

    const stageNumber = Math.min(...missingStageNumbers);
    const stageName = profileSectionTitle?.[stageNumber.toString() as keyof typeof profileSectionTitle];
    const missingStageNames = getStageNamesByStageNumber(stageNumber);

    if (stageName && missingStageNames && !checkStageSubmittedOrNot(stageNumber)) {
      return (
        <div className="fs-13 fw-400 text-black lh-sm">
          You are currently up to {stageName} and are missing: {missingStageNames}.
          <br />
          Finish setting up and submit your information for review.{" "}
          <span
            className="fw-700 text-decoration-underline cursor-pointer"
            onClick={handleStageRedirect}
          >
            Complete Stage {stageNumber}
          </span>
        </div>
      );
    }

    return (
      <div className="fs-13 fw-400 text-black lh-sm">
        {checkStageSubmittedOrNot(stageNumber) ? 
        `You have submitted your information for ${getStageName(stageNumber)} and it is in review right now.` :
        `You are currently up to ${getStageName(stageNumber)}. Finish setting up and submit your information for review.`}
        <br />
        Please note that between each stage, it will take up to 1 business day for a manual review to be completed by our consultants.
      </div>
    );
  }, [missingStageNumbers, completionstatus, profileData, missingDocs]);

  return (
    <>
      {/* {!isThirdStageActivated(candidateProfileStages) && !checkStageSubmittedOrNot(currentStage) && ( */}
      { (profileData?.verification_status !== 'verified' && alertMessage) && (
        <div className="d-flex">
          <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
            <Warning />
            {alertMessage}
          </div>
        </div>
      )}
    </>
  );
};

export default ActivationAlert;
